import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, query, where, Timestamp } from "firebase/firestore";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../components/ui/select";
import { setBookingStatus } from "../lib/bookingSync";

/**
 * Daily Booking Updates
 * ---------------------
 * Shows bookings for one day (today by default):
 *   - Completed: status "completed" and completedAt falls on the day.
 *   - In progress: status pending/confirmed and scheduled (dateTime) on the day.
 * Completed bookings are sorted on completedAt; in-progress ones follow,
 * sorted on their scheduled time.
 *
 * Only two date-bounded queries are run (dateTime in day, completedAt in day),
 * so the page never reads the whole bookings collection.
 */

const IN_PROGRESS_STATUSES = new Set(["pending", "confirmed"]);

const toDate = (val) => {
    if (!val) return null;
    if (typeof val.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
};

const formatTimestamp = (val) => {
    const d = toDate(val);
    if (!d) return "—";
    return d.toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
};

const localDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const dayBounds = (dateStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const start = new Date(y, m - 1, d);
    const end = new Date(y, m - 1, d + 1);
    return { start, end };
};

const normStatus = (s) => String(s || "pending").toLowerCase().trim();

const inRange = (date, { start, end }) => !!date && date >= start && date < end;

// Reads doc data with pending serverTimestamp() values estimated locally, so a
// booking just marked completed doesn't vanish until the server confirms.
const readDocs = (snapshot) =>
    snapshot.docs.map((d) => ({
        id: d.id,
        type: "bookings",
        ...d.data({ serverTimestamps: "estimate" }),
    }));

const useDailyBookings = (selectedDate) => {
    const [scheduled, setScheduled] = useState([]);
    const [completed, setCompleted] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const { start, end } = dayBounds(selectedDate);
        const s = Timestamp.fromDate(start);
        const e = Timestamp.fromDate(end);
        let pending = 2;
        const markLoaded = () => {
            if (pending > 0 && --pending === 0) setLoading(false);
        };
        const onError = (err) => {
            console.error("Error listening to daily bookings:", err);
            setError(err?.message || "Failed to load bookings");
            setLoading(false);
        };

        setLoading(true);
        setError(null);

        const unsubScheduled = onSnapshot(
            query(collection(db, "bookings"), where("dateTime", ">=", s), where("dateTime", "<", e)),
            (snap) => {
                setScheduled(readDocs(snap));
                markLoaded();
            },
            onError
        );
        const unsubCompleted = onSnapshot(
            query(collection(db, "bookings"), where("completedAt", ">=", s), where("completedAt", "<", e)),
            (snap) => {
                setCompleted(readDocs(snap));
                markLoaded();
            },
            onError
        );

        return () => {
            unsubScheduled();
            unsubCompleted();
        };
    }, [selectedDate]);

    const bookings = useMemo(() => {
        const bounds = dayBounds(selectedDate);
        const byId = new Map();
        [...scheduled, ...completed].forEach((b) => byId.set(b.id, b));

        const result = [];
        byId.forEach((b) => {
            const status = normStatus(b.status);
            const completedAt = toDate(b.completedAt);
            if (status === "completed" && inRange(completedAt, bounds)) {
                result.push({ ...b, _bucket: "completed", _completedAt: completedAt });
            } else if (IN_PROGRESS_STATUSES.has(status) && inRange(toDate(b.dateTime), bounds)) {
                result.push({ ...b, _bucket: "in_progress", _completedAt: null });
            }
        });
        return result;
    }, [scheduled, completed, selectedDate]);

    return { bookings, loading, error };
};

const BookingStatusUpdater = ({ booking }) => {
    const [status, setStatus] = useState(normStatus(booking.status));

    useEffect(() => setStatus(normStatus(booking.status)), [booking.status]);

    const handleChange = async (next) => {
        const prev = status;
        setStatus(next);
        try {
            await setBookingStatus(booking, next);
        } catch (err) {
            console.error("Error updating status:", err);
            setStatus(prev);
            alert(`Failed to update status: ${err?.message || "Unknown error"}`);
        }
    };

    return (
        <Select value={status} onValueChange={handleChange}>
            <SelectTrigger>{status}</SelectTrigger>
            <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Accepted</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="canceled">Rejected</SelectItem>
            </SelectContent>
        </Select>
    );
};

const STATUS_BADGE = {
    completed: { label: "Completed", cls: "bg-green-100 text-green-800 border-green-200" },
    confirmed: { label: "Accepted · In progress", cls: "bg-blue-100 text-blue-800 border-blue-200" },
    pending: { label: "Pending · In progress", cls: "bg-amber-100 text-amber-800 border-amber-200" },
};

const DailyBookingCard = ({ booking }) => {
    const status = normStatus(booking.status);
    const badge = STATUS_BADGE[status] || STATUS_BADGE.pending;
    const isCompleted = booking._bucket === "completed";

    return (
        <Card className="hover:shadow-md transition">
            <CardContent className="p-5">
                <div className="flex flex-col lg:flex-row justify-between gap-4">
                    <div className="flex-1 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${badge.cls}`}>
                                {badge.label}
                            </span>
                            <span className="text-xs bg-gray-100 text-gray-700 font-mono px-2 py-0.5 rounded border">
                                ID: {booking.id}
                            </span>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">{booking.categoryName || "Service Booking"}</h3>
                            {booking.secondaryCategoryName && (
                                <p className="text-sm text-gray-600 font-medium">
                                    Secondary: <span className="text-blue-600">{booking.secondaryCategoryName}</span>
                                </p>
                            )}
                            {booking.problemDescription && (
                                <p className="text-sm text-gray-600 mt-1 bg-gray-50 p-2 rounded border border-gray-100">
                                    {booking.problemDescription}
                                </p>
                            )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700 pt-2 border-t border-gray-100">
                            <div>
                                <span className="font-semibold text-gray-900">Provider:</span>{" "}
                                {booking.serviceProviderName ? (
                                    <span className="text-blue-700 font-medium">{booking.serviceProviderName}</span>
                                ) : (
                                    "Unassigned"
                                )}
                            </div>
                            <div>
                                <span className="font-semibold text-gray-900">Phone:</span>{" "}
                                {booking.phoneNumber || booking.userPhoneNumber || "N/A"}
                            </div>
                            <div className="sm:col-span-2">
                                <span className="font-semibold text-gray-900">Address:</span> {booking.address || "N/A"}
                            </div>
                            {booking.payableAmount !== undefined && (
                                <div>
                                    <span className="font-semibold text-gray-900">Payable:</span>{" "}
                                    <span className="font-bold text-green-700">₹{booking.payableAmount}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="lg:w-80 flex flex-col justify-between bg-blue-50/50 p-4 rounded-xl border border-blue-100 space-y-4">
                        <div className="space-y-2">
                            <div className="text-xs uppercase font-bold text-blue-900 tracking-wider border-b border-blue-200 pb-1">
                                Timestamps
                            </div>
                            <div className="bg-white p-2.5 rounded-lg border border-blue-100">
                                <div className="text-xs font-semibold text-gray-500">Completed at</div>
                                <div className={`text-sm ${isCompleted ? "font-bold text-green-700" : "text-gray-400"}`}>
                                    {isCompleted ? formatTimestamp(booking._completedAt) : "Not completed yet"}
                                </div>
                            </div>
                            <div className="bg-white p-2.5 rounded-lg border border-blue-100">
                                <div className="text-xs font-semibold text-gray-500">Scheduled for</div>
                                <div className="text-sm font-semibold text-gray-800">{formatTimestamp(booking.dateTime)}</div>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Update status</label>
                            <BookingStatusUpdater booking={booking} />
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

const StatTile = ({ label, value, tone }) => (
    <div className={`p-4 rounded-xl border shadow-sm text-center ${tone.box}`}>
        <p className={`text-xs font-semibold uppercase ${tone.label}`}>{label}</p>
        <p className={`text-3xl font-bold mt-1 ${tone.value}`}>{value}</p>
    </div>
);

const TONES = {
    gray: { box: "bg-white border-gray-100", label: "text-gray-400", value: "text-gray-900" },
    blue: { box: "bg-blue-50/30 border-blue-100", label: "text-blue-600", value: "text-blue-700" },
    green: { box: "bg-green-50/30 border-green-100", label: "text-green-600", value: "text-green-700" },
    emerald: { box: "bg-emerald-50/30 border-emerald-100", label: "text-emerald-600", value: "text-emerald-700" },
};

const FilterButton = ({ active, activeCls, onClick, children }) => (
    <button
        onClick={onClick}
        className={`px-4 py-1.5 rounded-md transition ${active ? `${activeCls} shadow-sm font-semibold` : "text-gray-600"}`}
    >
        {children}
    </button>
);

const DailyBookingUpdates = () => {
    const todayStr = localDateStr(new Date());
    const [selectedDate, setSelectedDate] = useState(todayStr);
    const [statusFilter, setStatusFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");

    const { bookings, loading, error } = useDailyBookings(selectedDate);

    const stats = useMemo(() => {
        const completedList = bookings.filter((b) => b._bucket === "completed");
        return {
            total: bookings.length,
            completed: completedList.length,
            inProgress: bookings.length - completedList.length,
            revenue: completedList.reduce((sum, b) => sum + (Number(b.payableAmount) || 0), 0),
        };
    }, [bookings]);

    const visible = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const dir = sortOrder === "desc" ? -1 : 1;
        return bookings
            .filter((b) => statusFilter === "all" || b._bucket === statusFilter)
            .filter((b) => {
                if (!q) return true;
                const hay = `${b.categoryName || ""} ${b.serviceProviderName || ""} ${b.address || ""} ${b.id} ${
                    b.phoneNumber || ""
                } ${b.userPhoneNumber || ""}`.toLowerCase();
                return hay.includes(q);
            })
            .sort((a, b) => {
                // Completed first, ordered by completion time; then in-progress
                // ordered by scheduled time.
                if (a._bucket !== b._bucket) return a._bucket === "completed" ? -1 : 1;
                const at = (a._completedAt || toDate(a.dateTime))?.getTime() ?? 0;
                const bt = (b._completedAt || toDate(b.dateTime))?.getTime() ?? 0;
                return (at - bt) * dir;
            });
    }, [bookings, statusFilter, searchQuery, sortOrder]);

    const isToday = selectedDate === todayStr;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Daily Booking Updates</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Bookings <span className="font-semibold text-blue-600">in progress</span> or{" "}
                        <span className="font-semibold text-green-600">completed</span> {isToday ? "today" : `on ${selectedDate}`},
                        sorted on completed date &amp; time.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700">Date:</label>
                    <input
                        type="date"
                        value={selectedDate}
                        max={todayStr}
                        onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    {!isToday && (
                        <button
                            onClick={() => setSelectedDate(todayStr)}
                            className="bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold px-3 py-2 text-xs rounded-lg transition"
                        >
                            Today
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatTile label="Total" value={stats.total} tone={TONES.gray} />
                <StatTile label="In progress" value={stats.inProgress} tone={TONES.blue} />
                <StatTile label="Completed" value={stats.completed} tone={TONES.green} />
                <StatTile
                    label="Completed revenue"
                    value={`₹${stats.revenue.toLocaleString("en-IN")}`}
                    tone={TONES.emerald}
                />
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center bg-gray-100 p-1 rounded-lg gap-1 text-sm font-medium">
                    <FilterButton active={statusFilter === "all"} activeCls="bg-white text-gray-900" onClick={() => setStatusFilter("all")}>
                        All ({stats.total})
                    </FilterButton>
                    <FilterButton
                        active={statusFilter === "in_progress"}
                        activeCls="bg-blue-600 text-white"
                        onClick={() => setStatusFilter("in_progress")}
                    >
                        In progress ({stats.inProgress})
                    </FilterButton>
                    <FilterButton
                        active={statusFilter === "completed"}
                        activeCls="bg-green-600 text-white"
                        onClick={() => setStatusFilter("completed")}
                    >
                        Completed ({stats.completed})
                    </FilterButton>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        type="text"
                        placeholder="Search service, provider, phone..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-full sm:w-64 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <label className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                        Completed time:
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        >
                            <option value="desc">Latest first</option>
                            <option value="asc">Earliest first</option>
                        </select>
                    </label>
                </div>
            </div>

            {error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
            ) : loading ? (
                <div className="text-center py-12 text-gray-500 text-lg">Loading daily booking updates...</div>
            ) : visible.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-500 shadow-sm">
                    <p className="text-xl font-bold text-gray-700 mb-1">No bookings found</p>
                    <p className="text-sm">No in-progress or completed bookings for {isToday ? "today" : selectedDate}.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {visible.map((b) => (
                        <DailyBookingCard key={b.id} booking={b} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default DailyBookingUpdates;
